/**
 * Real-time pose detection for camera viewfinder overlay.
 *
 * Uses MediaPipe PoseLandmarker in VIDEO mode with the lite model (~4MB)
 * for fast inference at 10-15fps on mobile. The heavier full model is
 * used separately for the final measurement extraction.
 */
import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';
import type { Landmark } from '@/types/measurements';

const LITE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task';
const VISION_WASM_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm';

/** How often to run inference (ms). ~12fps to save battery on mobile. */
const DETECTION_INTERVAL_MS = 80;

/** Minimum confidence for a landmark to count as "visible". */
const VISIBILITY_THRESHOLD = 0.65;

/** Key landmark indices for body zone checks. */
export const LANDMARK_INDEX = {
  nose: 0,
  leftShoulder: 11,
  rightShoulder: 12,
  leftElbow: 13,
  rightElbow: 14,
  leftWrist: 15,
  rightWrist: 16,
  leftHip: 23,
  rightHip: 24,
  leftKnee: 25,
  rightKnee: 26,
  leftAnkle: 27,
  rightAnkle: 28,
} as const;

/** Skeleton connections for drawing. Each pair is [fromIndex, toIndex]. */
export const SKELETON_CONNECTIONS: [number, number][] = [
  // Torso
  [11, 12], [11, 23], [12, 24], [23, 24],
  // Left arm
  [11, 13], [13, 15],
  // Right arm
  [12, 14], [14, 16],
  // Left leg
  [23, 25], [25, 27],
  // Right leg
  [24, 26], [26, 28],
];

export interface AlignmentStatus {
  head: boolean;
  shoulders: boolean;
  torso: boolean;
  arms: boolean;
  legs: boolean;
  distance: 'too_close' | 'perfect' | 'too_far' | 'unknown';
  allAligned: boolean;
  /** 0-1, fraction of key landmarks that are confident */
  overallScore: number;
}

export interface PoseFrame {
  landmarks: Landmark[];
  alignment: AlignmentStatus;
  /** Seconds the pose has been stable, 0 if unstable */
  stableSeconds: number;
}

export type PoseCallback = (frame: PoseFrame | null) => void;

/** Check if a landmark at the given index is visible. */
function isVisible(landmarks: Landmark[], index: number): boolean {
  const lm = landmarks[index];
  return !!lm && lm.visibility > VISIBILITY_THRESHOLD;
}

/** Calculate alignment status from landmarks (front photo). */
function checkAlignment(landmarks: Landmark[]): AlignmentStatus {
  const head = isVisible(landmarks, LANDMARK_INDEX.nose);

  const shoulders =
    isVisible(landmarks, LANDMARK_INDEX.leftShoulder) &&
    isVisible(landmarks, LANDMARK_INDEX.rightShoulder);

  const torso =
    shoulders &&
    isVisible(landmarks, LANDMARK_INDEX.leftHip) &&
    isVisible(landmarks, LANDMARK_INDEX.rightHip);

  const arms =
    isVisible(landmarks, LANDMARK_INDEX.leftElbow) &&
    isVisible(landmarks, LANDMARK_INDEX.rightElbow) &&
    isVisible(landmarks, LANDMARK_INDEX.leftWrist) &&
    isVisible(landmarks, LANDMARK_INDEX.rightWrist);

  const legs =
    isVisible(landmarks, LANDMARK_INDEX.leftKnee) &&
    isVisible(landmarks, LANDMARK_INDEX.rightKnee) &&
    isVisible(landmarks, LANDMARK_INDEX.leftAnkle) &&
    isVisible(landmarks, LANDMARK_INDEX.rightAnkle);

  // Distance check: body height as fraction of frame
  let distance: AlignmentStatus['distance'] = 'unknown';
  if (head && torso && legs) {
    const noseY = landmarks[LANDMARK_INDEX.nose]!.y;
    const ankleY = Math.max(
      landmarks[LANDMARK_INDEX.leftAnkle]!.y,
      landmarks[LANDMARK_INDEX.rightAnkle]!.y
    );
    const bodyHeight = ankleY - noseY;

    if (bodyHeight > 0.85) distance = 'too_close';
    else if (bodyHeight >= 0.45) distance = 'perfect';
    else distance = 'too_far';
  }

  // Count visible key landmarks
  const keyIndices = Object.values(LANDMARK_INDEX);
  const visibleCount = keyIndices.filter((i) => isVisible(landmarks, i)).length;
  const overallScore = visibleCount / keyIndices.length;

  const allAligned =
    head && shoulders && torso && arms && legs && distance === 'perfect';

  return { head, shoulders, torso, arms, legs, distance, allAligned, overallScore };
}

/**
 * Calculate alignment status for side photo (relaxed requirements).
 *
 * Standing sideways makes far-side landmarks invisible, so we only require:
 * - Head: nose visible
 * - Shoulders: ONE shoulder (near-side)
 * - Torso: one shoulder + one hip
 * - Arms: NOT required (we only need torso silhouette depth)
 * - Legs: one side only
 * - Distance: relaxed to 40-85% frame fill
 */
export function checkSideAlignment(landmarks: Landmark[]): AlignmentStatus {
  const head = isVisible(landmarks, LANDMARK_INDEX.nose);

  const shoulders =
    isVisible(landmarks, LANDMARK_INDEX.leftShoulder) ||
    isVisible(landmarks, LANDMARK_INDEX.rightShoulder);

  const torso =
    shoulders &&
    (isVisible(landmarks, LANDMARK_INDEX.leftHip) ||
      isVisible(landmarks, LANDMARK_INDEX.rightHip));

  // Arms NOT required for side photo
  const arms = true;

  const legs =
    (isVisible(landmarks, LANDMARK_INDEX.leftKnee) ||
      isVisible(landmarks, LANDMARK_INDEX.rightKnee)) &&
    (isVisible(landmarks, LANDMARK_INDEX.leftAnkle) ||
      isVisible(landmarks, LANDMARK_INDEX.rightAnkle));

  // Distance check: relaxed range (40-85%)
  let distance: AlignmentStatus['distance'] = 'unknown';
  if (head && torso && legs) {
    const noseY = landmarks[LANDMARK_INDEX.nose]!.y;
    const lAnkle = landmarks[LANDMARK_INDEX.leftAnkle]!;
    const rAnkle = landmarks[LANDMARK_INDEX.rightAnkle]!;
    const ankleY =
      lAnkle.visibility > rAnkle.visibility ? lAnkle.y : rAnkle.y;
    const bodyHeight = ankleY - noseY;

    if (bodyHeight > 0.85) distance = 'too_close';
    else if (bodyHeight >= 0.40) distance = 'perfect';
    else distance = 'too_far';
  }

  const keyIndices = Object.values(LANDMARK_INDEX);
  const visibleCount = keyIndices.filter((i) => isVisible(landmarks, i)).length;
  const overallScore = visibleCount / keyIndices.length;

  const allAligned =
    head && shoulders && torso && arms && legs && distance === 'perfect';

  return { head, shoulders, torso, arms, legs, distance, allAligned, overallScore };
}

/**
 * Real-time pose detection controller.
 * Call start() with a video element, provides landmarks via callback.
 */
export class RealtimePose {
  private landmarker: PoseLandmarker | null = null;
  private animFrameId = 0;
  private lastDetectTime = 0;
  private callback: PoseCallback;
  private running = false;
  private loading = false;
  private captureType: 'front' | 'side';

  // Stability tracking
  private stableStart = 0;
  private lastLandmarks: Landmark[] | null = null;

  constructor(callback: PoseCallback, captureType: 'front' | 'side' = 'front') {
    this.callback = callback;
    this.captureType = captureType;
  }

  /** Switch between front/side alignment checks without recreating the instance. */
  setCaptureType(type: 'front' | 'side'): void {
    this.captureType = type;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  get isReady(): boolean {
    return !!this.landmarker;
  }

  /** Load the lite model. Can be called early to pre-warm. */
  async load(): Promise<void> {
    if (this.landmarker || this.loading) return;
    this.loading = true;

    try {
      const vision = await FilesetResolver.forVisionTasks(VISION_WASM_URL);

      try {
        this.landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: LITE_MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numPoses: 1,
        });
      } catch {
        // GPU fallback to CPU
        this.landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: LITE_MODEL_URL, delegate: 'CPU' },
          runningMode: 'VIDEO',
          numPoses: 1,
        });
      }
    } catch {
      // Model load failed — caller will see isReady === false
      this.landmarker = null;
    } finally {
      this.loading = false;
    }
  }

  /** Start detection loop on a video element. */
  start(video: HTMLVideoElement): void {
    if (this.running) return;
    this.running = true;
    this.stableStart = 0;
    this.lastLandmarks = null;

    const loop = () => {
      if (!this.running) return;

      const now = performance.now();
      if (
        this.landmarker &&
        video.readyState >= 2 &&
        now - this.lastDetectTime >= DETECTION_INTERVAL_MS
      ) {
        this.lastDetectTime = now;

        try {
          const result = this.landmarker.detectForVideo(video, now);

          if (result.landmarks && result.landmarks.length > 0) {
            const landmarks: Landmark[] = result.landmarks[0]!.map((lm) => ({
              x: lm.x,
              y: lm.y,
              z: lm.z ?? 0,
              visibility: lm.visibility ?? 0,
            }));

            const alignment = this.captureType === 'side'
              ? checkSideAlignment(landmarks)
              : checkAlignment(landmarks);
            const stableSeconds = this.updateStability(landmarks, alignment);

            this.callback({ landmarks, alignment, stableSeconds });
          } else {
            this.stableStart = 0;
            this.lastLandmarks = null;
            this.callback(null);
          }
        } catch {
          // Inference error — skip frame
        }
      }

      this.animFrameId = requestAnimationFrame(loop);
    };

    this.animFrameId = requestAnimationFrame(loop);
  }

  /** Stop detection loop. */
  stop(): void {
    this.running = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
    this.stableStart = 0;
    this.lastLandmarks = null;
  }

  /** Dispose model and free memory. */
  dispose(): void {
    this.stop();
    if (this.landmarker) {
      this.landmarker.close();
      this.landmarker = null;
    }
  }

  /** Track how long the pose has been stable. */
  private updateStability(
    landmarks: Landmark[],
    alignment: AlignmentStatus
  ): number {
    if (!alignment.allAligned) {
      this.stableStart = 0;
      this.lastLandmarks = landmarks;
      return 0;
    }

    // Check movement against previous frame
    if (this.lastLandmarks) {
      let totalMovement = 0;
      const keyIndices = Object.values(LANDMARK_INDEX);
      for (const idx of keyIndices) {
        const curr = landmarks[idx];
        const prev = this.lastLandmarks[idx];
        if (curr && prev) {
          totalMovement += Math.abs(curr.x - prev.x) + Math.abs(curr.y - prev.y);
        }
      }
      const avgMovement = totalMovement / keyIndices.length;

      // If moving too much, reset stability timer
      if (avgMovement > 0.015) {
        this.stableStart = 0;
        this.lastLandmarks = landmarks;
        return 0;
      }
    }

    this.lastLandmarks = landmarks;

    if (this.stableStart === 0) {
      this.stableStart = performance.now();
    }

    return (performance.now() - this.stableStart) / 1000;
  }
}
